// Renders one magnified "eye tile" per locked-on person, all at once: each tile
// shows a live, zoomed-in crop of that person's eyes (from the eye-region
// landmarks), plus a gaze-match percentage, their approximate range, how far
// their line of sight lands from the phone, and a rough height estimate.
//
// This is the "lock onto multiple faces and zoom into each of their eyes"
// behaviour: instead of one cinematic push-in, every watcher gets a persistent
// magnified eye view, added/updated/removed as people look at you and away.

import type { TrackedFace } from "../types";
import type { GazeGeometry } from "../vision/geometry";

const TILE_W = 208;
const TILE_H = 88;

interface Tile {
  root: HTMLElement;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  head: HTMLElement;
  fill: HTMLElement;
  meta: HTMLElement;
}

export class EyeTiles {
  private tiles = new Map<number, Tile>();

  constructor(private container: HTMLElement) {}

  /**
   * Sync the set of eye tiles to the currently locked faces. `geomOf` returns
   * the gaze geometry for a face (kept out of here so all the maths lives in
   * one place). Call once per frame while the loop is running.
   */
  sync(watchers: TrackedFace[], video: HTMLVideoElement, geomOf: (f: TrackedFace) => GazeGeometry): void {
    const live = new Set(watchers.map((w) => w.id));

    // Remove tiles for people who looked away.
    for (const [id, tile] of this.tiles) {
      if (!live.has(id)) {
        tile.root.remove();
        this.tiles.delete(id);
      }
    }

    for (const w of watchers) {
      let tile = this.tiles.get(w.id);
      if (!tile) {
        tile = this.create(w.id);
        this.tiles.set(w.id, tile);
      }
      this.drawEyes(tile, w, video);

      const g = geomOf(w);
      tile.head.textContent = `#${w.id} · ${g.matchPct}% match`;
      tile.fill.style.width = `${g.matchPct}%`;
      const range = g.rangeM > 0 ? `${g.rangeM.toFixed(1)}m` : "—";
      tile.meta.textContent = `${range} · lands ${g.gazeMissCm}cm off · h≈${g.estHeightM.toFixed(2)}m`;
    }
  }

  private create(id: number): Tile {
    const root = document.createElement("div");
    root.className = "eye-tile";

    const head = document.createElement("div");
    head.className = "eye-head";
    head.textContent = `#${id}`;

    const canvas = document.createElement("canvas");
    canvas.className = "eye-canvas";
    canvas.width = TILE_W;
    canvas.height = TILE_H;

    const track = document.createElement("div");
    track.className = "eye-track";
    const fill = document.createElement("div");
    fill.className = "eye-fill";
    track.appendChild(fill);

    const meta = document.createElement("div");
    meta.className = "eye-meta";

    root.append(head, canvas, track, meta);
    this.container.appendChild(root);

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    return { root, canvas, ctx, head, fill, meta };
  }

  /** Draw a padded, cover-fitted crop of the eye region into the tile canvas. */
  private drawEyes(tile: Tile, w: TrackedFace, video: HTMLVideoElement): void {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const { ctx } = tile;
    ctx.clearRect(0, 0, TILE_W, TILE_H);
    if (vw === 0 || vh === 0) return;

    // The eye box is short vertically, so pad more on Y to show full eyes + brows.
    const b = w.eyesBBox;
    const padX = b.w * 0.25;
    const padY = b.h * 1.2;
    let sx = (b.x - padX) * vw;
    let sy = (b.y - padY) * vh;
    let sw = (b.w + 2 * padX) * vw;
    let sh = (b.h + 2 * padY) * vh;

    // Clamp the source rect inside the frame.
    if (sx < 0) { sw += sx; sx = 0; }
    if (sy < 0) { sh += sy; sy = 0; }
    if (sx + sw > vw) sw = vw - sx;
    if (sy + sh > vh) sh = vh - sy;
    if (sw < 4 || sh < 4) return;

    // Cover-fit the crop into the tile.
    const scale = Math.max(TILE_W / sw, TILE_H / sh);
    const dw = sw * scale;
    const dh = sh * scale;
    const dx = (TILE_W - dw) / 2;
    const dy = (TILE_H - dh) / 2;
    try {
      ctx.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);
    } catch {
      // A transient decode error must never break the loop.
    }
  }
}
